/**
 * Sign-in and onboarding copy: the authentication page, its failure
 * categories and the invitation flow, and the first-visit onboarding.
 */

export interface SignInCopy {
  readonly auth: {
    readonly title: string;
    readonly sub: string;
    readonly limited: string;
    readonly continueWithGoogle: string;
    readonly continueWithApple: string;
    readonly redirecting: string;
    readonly verifying: string;
    readonly signedIn: string;
    readonly noProviders: string;
    readonly errorHeading: string;
    /** The three categories the backend actually redirects with. */
    readonly errorUnavailable: string;
    readonly errorRetry: string;
    readonly errorFailed: string;
    readonly pendingTitle: string;
    readonly pendingBody: string;
    readonly backToSignIn: string;
    readonly invitationNoted: string;
  };

  readonly onboarding: {
    readonly title: string;
    readonly stepLabel: (current: number, total: number) => string;
    readonly steps: readonly {
      readonly title: string;
      readonly body: string;
    }[];
    readonly next: string;
    readonly back: string;
    readonly finish: string;
    readonly skipNote: string;
  };
}

export const SIGN_IN_COPY_EN: SignInCopy = {
  auth: {
    title: 'Sign in to Mouja',
    sub: 'Use the Google or Apple account associated with your authorized email.',
    limited:
      'Access is limited to authorized contributors. There is no public account creation.',
    continueWithGoogle: 'Continue with Google',
    continueWithApple: 'Continue with Apple',
    redirecting: 'Redirecting to your provider…',
    verifying: 'Verifying your sign-in…',
    signedIn: 'Signed in. Taking you to your dashboard…',
    noProviders:
      'Sign-in is not available right now. No identity provider is configured.',
    errorHeading: 'Sign-in did not complete',
    errorUnavailable:
      'Sign-in is not available right now. Please try again later or contact the person who invited you.',
    errorRetry:
      'The identity provider could not be reached. Check your connection and try again.',
    errorFailed:
      'The sign-in could not be completed. If you followed an invitation link, request a fresh one — invitations are single-use and expire.',
    pendingTitle: 'An owner needs to confirm your access',
    pendingBody:
      'Your identity was verified, but the email your provider shared does not match the invitation. An owner has been asked to confirm it — you will be able to sign in once they do.',
    backToSignIn: 'Back to sign in',
    invitationNoted: 'Your invitation will be applied when you continue.',
  },

  onboarding: {
    title: 'Welcome to the Mouja workspace',
    stepLabel: (current, total) => `Step ${current} of ${total}`,
    steps: [
      {
        title: 'What this workspace is',
        body: 'Mouja coordinates contributor work on an Arabic-first commerce intelligence product. You will find your work on the task board: development tasks build the product, R&D tasks investigate what to build, and annotation tasks improve the data behind it. Everything you do here is recorded in an append-only history.',
      },
      {
        title: 'How work moves',
        body: 'Anyone may propose a task into the backlog. An owner approves proposals into Ready and sets their priority. You claim Ready work to start it, submit it for review when it is done, and a peer — never yourself — reviews it to completion. GitHub links are entered by hand and clearly marked as unverified.',
      },
      {
        title: 'What you can see',
        body: 'Your role decides which work you see, and the server enforces it — the board, counts and feeds you receive are already filtered. Confidential processing internals are not part of this workspace, and nothing you need to contribute depends on them.',
      },
    ],
    next: 'Next',
    back: 'Back',
    finish: 'Go to my dashboard',
    skipNote: 'You can reopen this introduction anytime from Help.',
  },
};

export const SIGN_IN_COPY_AR: SignInCopy = {
  auth: {
    title: 'تسجيل الدخول إلى موجة',
    sub: 'استخدم حساب Google أو Apple المرتبط ببريدك المصرّح به.',
    limited: 'الوصول مقصور على المساهمين المصرّح لهم. لا يوجد تسجيل عام.',
    continueWithGoogle: 'المتابعة عبر Google',
    continueWithApple: 'المتابعة عبر Apple',
    redirecting: 'جارٍ تحويلك إلى مزوّد الهوية…',
    verifying: 'جارٍ التحقق من تسجيل دخولك…',
    signedIn: 'تم تسجيل الدخول. جارٍ نقلك إلى لوحة العمل…',
    noProviders: 'تسجيل الدخول غير متاح حاليًا. لم يُهيّأ أي مزوّد هوية.',
    errorHeading: 'لم يكتمل تسجيل الدخول',
    errorUnavailable: 'تسجيل الدخول غير متاح حاليًا. حاول لاحقًا أو تواصل مع من دعاك.',
    errorRetry: 'تعذّر الوصول إلى مزوّد الهوية. تحقق من اتصالك وحاول مجددًا.',
    errorFailed:
      'تعذّر إكمال تسجيل الدخول. إذا استخدمت رابط دعوة فاطلب رابطًا جديدًا — الدعوات تُستخدم مرة واحدة وتنتهي صلاحيتها.',
    pendingTitle: 'يحتاج أحد المالكين إلى تأكيد وصولك',
    pendingBody:
      'تم التحقق من هويتك، لكن البريد الذي شاركه مزوّدك لا يطابق الدعوة. طُلب من أحد المالكين تأكيد ذلك — ستتمكن من الدخول فور موافقته.',
    backToSignIn: 'العودة إلى تسجيل الدخول',
    invitationNoted: 'ستُطبَّق دعوتك عند المتابعة.',
  },

  onboarding: {
    title: 'مرحبًا بك في مساحة عمل موجة',
    stepLabel: (current, total) => `الخطوة ${current} من ${total}`,
    steps: [
      {
        title: 'ما هذه المساحة',
        body: 'تنسّق موجة عمل المساهمين في منتج ذكاء تجاري عربي أولًا. ستجد عملك في لوحة المهام: مهام التطوير تبني المنتج، ومهام البحث والتطوير تستكشف ما يُبنى، ومهام التصنيف تحسّن البيانات خلفه. كل ما تفعله هنا يُسجَّل في تاريخ لا يُعدَّل.',
      },
      {
        title: 'كيف يتحرك العمل',
        body: 'يمكن لأي عضو اقتراح مهمة إلى قائمة المقترحات. يعتمد المالك المقترحات إلى «جاهزة» ويحدد أولويتها. تستلم أنت العمل الجاهز لتبدأه، وترسله للمراجعة عند اكتماله، ويراجعه زميل — وليس أنت — حتى الاكتمال. روابط GitHub تُدخل يدويًا وتُعلَّم بوضوح أنها غير متحقق منها.',
      },
      {
        title: 'ما الذي تراه',
        body: 'دورك يحدد العمل الذي تراه، والخادم يفرض ذلك — اللوحة والأعداد والسجلات التي تصلك مُرشّحة مسبقًا. تفاصيل المعالجة السرية ليست جزءًا من هذه المساحة، ولا يعتمد عليها شيء مما تحتاجه للمساهمة.',
      },
    ],
    next: 'التالي',
    back: 'السابق',
    finish: 'الانتقال إلى لوحة العمل',
    skipNote: 'يمكنك إعادة فتح هذه المقدمة في أي وقت من «المساعدة».',
  },
};
